/* eslint-disable no-unused-vars */
/**
 * Imports the necessary React components and hooks for a React component.
 * @module React
 * @module useState
 * @module useNavigate
 * @module useTranslation
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import parser from "html-react-parser";
import { Helmet } from 'react-helmet';

import { showToast } from "../../store/toast/actions";

import { PublicClientApplication } from "@azure/msal-browser";
import LoginComponent from "./loginComponent";

// import "./auth.scss";
import { login } from "../../store/auth/actions";
import Constant from "../../util/constant";

/**
 * A functional component that handles the login functionality.
 * @param {{object}} props - The props passed to the component.
 * @returns The rendered LoginComponent with the necessary props.
 */
const Login = (props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [loaderButtonShow, setLoaderButtonShow] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [error, setError] = useState('');
  const openErrorModal = () => setShowErrorModal(true);
  const closeErrorModal = () => setShowErrorModal(false);

  /**
   * Configuration object for Microsoft Authentication Library (MSAL).
   * @type {{auth: {clientId: string, authority: string, redirectUri: string}, cache: {cacheLocation: string}}}
   */

  // Get the redirect URI - must match exactly what's configured in Azure AD
  const redirectUri = window.location.origin;

  const msalConfig = {
    auth: {
      clientId: process.env.REACT_APP_MICROSOFT_CLIENT_ID,
      authority: process.env.REACT_APP_MICROSOFT_AUTHORITY,
      redirectUri: redirectUri,
    },
    cache: {
      cacheLocation: "sessionStorage",
    },
    system: {
      allowNativeBroker: false, // Disable native broker
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return; // Don't log PII
          if (level === 0) { // Error level
            console.error('MSAL Error:', message);
          } else if (level === 1) { // Warning level
            console.warn('MSAL Warning:', message);
          } else if (level === 2) { // Info level
            console.log('MSAL Info:', message);
          }
        },
        logLevel: 2, // Info level logging for debugging
      },
    },
  };

  const msalInstance = new PublicClientApplication(msalConfig);
  const [msalInitialized, setMsalInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  const clearInteractionState = useCallback(() => {
    const msalKeys = Object.keys(sessionStorage).filter(key => key.startsWith('msal.'));
    msalKeys.forEach(key => {
      try {
        const value = JSON.parse(sessionStorage.getItem(key));
        if (value?.interactionType) {
          sessionStorage.removeItem(key);
        }
      } catch (e) {
        // Ignore parsing errors
      }
    });
  }, []);

  const processLoginResponse = useCallback((response) => {
    if (!response || !response.account) {
      console.error('Invalid login response:', response);
      setLoaderButtonShow(false);
      dispatch(
        showToast({
          message: t("LOGIN_FAILED"),
          type: "error",
        })
      );
      return;
    }

    localStorage?.setItem(Constant.LOCALSTORAGEKEYS.NAME, response.account.name || response.account.username);
    
    if (response.accessToken) {
      const params = {
        emailid: response.account.username,
        microsoftid: response.account.tenantId,
        devicetoken: ''
      };
      
      dispatch(login(params, (res) => {
        window.localStorage.setItem(Constant.LOCALSTORAGEKEYS.FULLNAME, res?.data?.fullname);
        navigate('/noticemanagement');
      }, (err) => {
        openErrorModal(true);
        setLoaderButtonShow(false);
        setError(err?.response?.data?.message !== undefined ? parser(err?.response?.data?.message) : t("LOGIN_FAILED"));
      }));
    } else {
      console.warn('No access token in response, attempting silent token acquisition...');
      msalInstance.acquireTokenSilent({
        scopes: ["User.Read"],
        account: response.account,
      }).then((tokenResponse) => {
        if (tokenResponse && tokenResponse.accessToken) {
          const params = {
            emailid: response.account.username,
            microsoftid: response.account.tenantId,
            devicetoken: ''
          };
          
          dispatch(login(params, (res) => {
            window.localStorage.setItem(Constant.LOCALSTORAGEKEYS.FULLNAME, res?.data?.fullname);
            navigate('/noticemanagement');
          }, (err) => {
            openErrorModal(true);
            setLoaderButtonShow(false);
            setError(err?.response?.data?.message !== undefined ? parser(err?.response?.data?.message) : t("LOGIN_FAILED"));
          }));
        } else {
          setLoaderButtonShow(false);
          dispatch(
            showToast({
              message: t("LOGIN_FAILED_NO_TOKEN"),
              type: "error",
            })
          );
        }
      }).catch((error) => {
        console.error('Silent token acquisition failed:', error);
        setLoaderButtonShow(false);
        dispatch(
          showToast({
            message: t("LOGIN_FAILED_NO_TOKEN"),
            type: "error",
          })
        );
      });
    }
  }, [dispatch, navigate, openErrorModal, setLoaderButtonShow, t, msalInstance]);


  // Initialize MSAL on mount and handle redirect callbacks
  useEffect(() => {
    if (isInitializing || msalInitialized) {
      return;
    }

    const initializeMsal = async () => {
      setIsInitializing(true);
      try {
        if (!msalInstance.getConfiguration()) {
          await msalInstance.initialize();
        }
        
        setMsalInitialized(true);
        
        // Always check for redirect response on mount
        // This handles the callback after Microsoft redirects back
        try {
          const redirectResponse = await msalInstance.handleRedirectPromise();
          if (redirectResponse && redirectResponse.account) {
            setLoaderButtonShow(true);
            processLoginResponse(redirectResponse);
            setIsInitializing(false);
            return;
          }
        } catch (redirectError) {
          console.error('Redirect handling error:', redirectError);
          clearInteractionState();
        }
        
        clearInteractionState();
      } catch (error) {
        console.error('MSAL initialization error:', error);
        setMsalInitialized(true);
      } finally {
        setIsInitializing(false);
      }
    };
    
    initializeMsal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  /**
   * Performs a Microsoft login using MSAL library redirect flow.
   * @returns None
   * @throws {Error} If there is an error during the login process.
   */
  const microsoftLogin = async () => {
    if (!msalInstance) {
      return;
    }

    if (!msalInitialized) {
      dispatch(
        showToast({
          message: t("PLEASE_WAIT_INITIALIZING"),
          type: "info",
        })
      );
      return;
    }

    setLoaderButtonShow(true);
    
    try {
      if (!msalInitialized && isInitializing) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      if (!msalInstance.getConfiguration()) {
        await msalInstance.initialize();
      }
      
      clearInteractionState();
      
      // Use loginRedirect instead of loginPopup
      // This will redirect the entire page to Microsoft, then redirect back
      await msalInstance.loginRedirect({
        scopes: ["User.Read"],
        prompt: 'select_account',
      });
      
      // Note: loginRedirect doesn't return a promise - it redirects the page
      // The redirect callback will be handled by handleRedirectPromise() in useEffect
      // So we don't need to process a response here
      
    } catch (error) {
      setLoaderButtonShow(false); 
      console.error('Login redirect error:', error);
      
      if (error?.errorCode === "interaction_in_progress") {
        clearInteractionState();
        
        dispatch(
          showToast({
            message: t("INTERACTION_IN_PROGRESS"),
            type: "error",
          })
        );
      } else {
        dispatch(
          showToast({
            message: error?.message || t("LOGIN_FAILED"),
            type: "error",
          })
        );
      }
    }
  };

  /**
   * Renders a LoginComponent with the provided props.
   * @param {{microsoftLogin}} microsoftLogin - The microsoftLogin component.
   * @param {{showErrorModal}} showErrorModal - The function to show the error modal.
   * @param {{openErrorModal}} openErrorModal - The function to open the error modal.
   * @param {{closeErrorModal}} closeErrorModal - The function to close the error modal.
   * @returns The rendered LoginComponent.
   */
  return (
    <>
    <Helmet>
      <title>Log In | ScotRail Safety Notice Portal</title>
    </Helmet>
    <LoginComponent
      microsoftLogin={microsoftLogin}
      showErrorModal={showErrorModal}
      openErrorModal={openErrorModal}
      closeErrorModal={closeErrorModal}
      modalDescription={error}
      loaderButtonShow={loaderButtonShow}
    />
 </>);
};

export default Login;
